import { useId, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AlertCircle, Info, Pencil, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import { useSaveExpertise } from "../../../api/hooks";
import { isRealisticPhone, isValidEmail } from "../../../lib/contact";
import { MANDATE_YEAR_MIN, mandateYearMax } from "../../../lib/mandate";
import { LONG_TEXT_LIMIT, wordLength } from "../../../lib/text";
import type {
  CatalogueIssue,
  CatalogueThematic,
  ExpertiseInput,
  ExpertiseState,
  MandateClient,
  RegistrationProfile,
} from "../../../types";
import { Badge } from "../../ui/Badge";
import {
  CheckRow,
  NumberRow,
  ScopeBox,
  TextareaRow,
  TextRow,
} from "./controls";
import { DeclarationModal } from "./DeclarationModal";

type Ref = { id: string; name: string };
type OrgType = { value: string; label: string };

interface EnjeuState {
  mandate_count: number | null;
  sectors: string[];
  organization_types: string[];
  mandate: {
    name: string;
    year: number | null;
    description: string;
    is_accompaniment: boolean;
    clients: MandateClient[];
  };
}

type FieldErrors = Record<string, string>;

/**
 * Every message in a server error payload, flattened. A mandate's errors are
 * mostly `{field: message}`, but its reference clients come back nested
 * (`clients: {0: {email: message}}`) — rendering that object as a React child
 * would crash the step.
 */
function errorMessages(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(errorMessages);
  if (value && typeof value === "object")
    return Object.values(value).flatMap(errorMessages);
  return [];
}

/**
 * The mandate rules of `registration_sync.validate_expertise`, checked here so
 * the mandate modal can refuse to close on an incomplete mandate instead of
 * the expert finding out after saving the whole step. `required` is false for
 * a pre-approved enjeu: it needs no mandate, but one it describes still obeys
 * the length and contact rules. The server re-checks everything on save.
 */
function validateMandate(
  enjeu: EnjeuState,
  required: boolean,
  t: TFunction,
): FieldErrors {
  const m = enjeu.mandate;
  const errors: FieldErrors = {};
  if (required) {
    if (!m.name.trim()) errors.name = t("expert.register.validation.mandate_name");
    if (m.year == null)
      errors.year = t("expert.register.validation.mandate_year");
    else if (
      !Number.isInteger(m.year) ||
      m.year < MANDATE_YEAR_MIN ||
      m.year > mandateYearMax()
    )
      errors.year = t("expert.register.validation.mandate_year_range", {
        min: MANDATE_YEAR_MIN,
        max: mandateYearMax(),
      });
    if (!m.description.trim())
      errors.description = t("expert.register.validation.mandate_description");
    if (!m.is_accompaniment)
      errors.is_accompaniment = t("expert.register.validation.mandate_accompaniment");
  }
  if (wordLength(m.description) > LONG_TEXT_LIMIT)
    errors.description = t("expert.register.validation.too_long", {
      limit: LONG_TEXT_LIMIT,
    });
  for (const c of m.clients) {
    if (c.email.trim() && !isValidEmail(c.email.trim()))
      errors.client_email = t("expert.register.validation.client_email");
    if (c.phone.trim() && !isRealisticPhone(c.phone.trim()))
      errors.client_phone = t("expert.register.validation.client_phone");
  }
  return errors;
}

function emptyEnjeu(): EnjeuState {
  return {
    mandate_count: null,
    sectors: [],
    organization_types: [],
    mandate: {
      name: "",
      year: null,
      description: "",
      is_accompaniment: false,
      clients: [],
    },
  };
}

function seed(
  state: ExpertiseState,
  preapproved: Set<string>,
  offered: Set<string>,
  defaultScope: Pick<EnjeuState, "sectors" | "organization_types">,
): Record<string, EnjeuState> {
  const out: Record<string, EnjeuState> = {};
  for (const row of state.expertises) {
    // An expertise row for an enjeu this step doesn't offer can't be rendered,
    // so carrying it in state would silently resubmit it — the Django step drops
    // it instead (it isn't posted back, and sync deletes what isn't selected).
    if (!offered.has(row.issue)) continue;
    out[row.issue] = {
      mandate_count: row.mandate_count,
      sectors: row.sectors,
      organization_types: row.organization_types,
      mandate: row.mandate
        ? {
            name: row.mandate.name,
            // A mandate saved before the year was asked for has none: the field
            // comes up empty and its owner has to fill it in to save again.
            year: row.mandate.year ?? null,
            description: row.mandate.description,
            is_accompaniment: row.mandate.is_accompaniment,
            clients: row.mandate.clients,
          }
        : emptyEnjeu().mandate,
    };
  }
  // Pre-approved enjeux (CQDD) are always pre-selected, even without an
  // expertise row, so they never drop off the form (parity with the Django
  // registration step). They inherit the full registration scope like a
  // manually-toggled enjeu, so their per-enjeu scope submits non-empty and
  // passes validate_scope.
  for (const id of preapproved) {
    if (!out[id]) out[id] = { ...emptyEnjeu(), ...defaultScope };
  }
  return out;
}

/**
 * Step 2 — declare enjeux with a per-enjeu reference mandate and scope. Each
 * selected enjeu needs ≥1 sector, size and org type (drawn from the step-1 pool)
 * and — unless CQDD pre-approved it — a described mandate. Saved via PUT, which
 * revalidates server-side; field errors are echoed inline.
 */
export function ExpertiseStep({
  catalogue,
  expertiseState,
  poolSectors,
  poolOrgTypes,
  preapproved,
  profile,
  submitMode,
  onSubmitted,
  onBack,
  onOrganizationIncomplete,
  onServiceRangeIncomplete,
}: {
  catalogue: CatalogueThematic[];
  expertiseState: ExpertiseState;
  poolSectors: Ref[];
  poolOrgTypes: OrgType[];
  preapproved: Set<string>;
  profile: RegistrationProfile;
  // Draft/rejected: confirm the declaration in a modal and (re)submit for
  // review. Otherwise (submitted/validated): save the expertise edit in place.
  submitMode: boolean;
  onSubmitted: () => void;
  onBack: () => void;
  // A submit refused over a blank organization field: back to that step.
  onOrganizationIncomplete?: (message: string) => void;
  // Refused over the provinces answer: back to the service-range step.
  onServiceRangeIncomplete?: (message: string) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveExpertise();
  const [selected, setSelected] = useState<Record<string, EnjeuState>>(() => {
    // What this step actually offers. The catalogue already excludes enjeux with
    // no can_have_experts action (registration_sync.offered_issues, shared with
    // the Django step); the has_goals check keeps the guard local so a catalogue
    // regression can't pre-select a dead enjeu. Not the nested goals: they are
    // sent only for enjeux the CQDD approved.
    const offered = new Set(
      catalogue.flatMap((thematic) =>
        thematic.issues
          .filter((issue) => issue.has_goals)
          .map((issue) => issue.id),
      ),
    );
    const offeredPreapproved = new Set(
      [...preapproved].filter((id) => offered.has(id)),
    );
    // Inherit the full registration scope (parity with toggleIssue) so the
    // auto-selected enjeu's scope is non-empty at submit time.
    const defaultScope = {
      sectors: poolSectors.map((s) => s.id),
      organization_types: poolOrgTypes.map((o) => o.value),
    };
    return seed(expertiseState, offeredPreapproved, offered, defaultScope);
  });
  const [mandateErrors, setMandateErrors] = useState<Record<string, FieldErrors>>({});
  const [scopeErrors, setScopeErrors] = useState<Record<string, FieldErrors>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // The declaration is the final Part-1 step, shown in a modal opened from the
  // submit button (parity with the legacy app), only when (re)submitting.
  const [declarationOpen, setDeclarationOpen] = useState(false);

  const toggleIssue = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = { ...prev };
      // A newly selected enjeu inherits the full registration scope (all sectors
      // and org types) — most experts serve the same scope per enjeu, so
      // it's pre-checked and hidden; they reveal it only to narrow it.
      if (on)
        next[id] = {
          ...emptyEnjeu(),
          sectors: poolSectors.map((s) => s.id),
          organization_types: poolOrgTypes.map((o) => o.value),
        };
      else delete next[id];
      return next;
    });

  const patch = (id: string, updater: (e: EnjeuState) => EnjeuState) =>
    setSelected((prev) => ({ ...prev, [id]: updater(prev[id] ?? emptyEnjeu()) }));

  const handleContinue = () => {
    setFormError(null);
    setMandateErrors({});
    setScopeErrors({});
    const ids = Object.keys(selected);
    if (ids.length === 0) {
      setFormError(t("expert.register.validation.no_enjeu"));
      return;
    }
    // Reference clients must carry a valid email and a realistic phone number,
    // and a description must fit; required fields are left to the server here.
    const clientErrors: Record<string, FieldErrors> = {};
    for (const id of ids) {
      const errors = validateMandate(selected[id]!, false, t);
      if (Object.keys(errors).length > 0) clientErrors[id] = errors;
    }
    if (Object.keys(clientErrors).length > 0) {
      setMandateErrors(clientErrors);
      // Django's summary for the same errors (registration_sync.validate_expertise).
      setFormError(t("expert.register.validation.fix_indicated"));
      return;
    }
    const expertises: ExpertiseInput[] = ids.map((id) => {
      const e = selected[id]!;
      const hasMandate =
        e.mandate.name.trim() ||
        e.mandate.year != null ||
        e.mandate.description.trim() ||
        e.mandate.clients.length > 0;
      return {
        issue: id,
        mandate_count: e.mandate_count,
        sectors: e.sectors,
        organization_types: e.organization_types,
        mandate: hasMandate ? e.mandate : null,
      };
    });
    save.mutate(expertises, {
      // Draft/rejected: expertise saved — open the declaration modal to finalize
      // and (re)submit. Otherwise the edit is saved in place (staged server-side
      // for a validated expert); return to where the wizard was opened from.
      onSuccess: () => (submitMode ? setDeclarationOpen(true) : onSubmitted()),
      onError: (err: unknown) => {
        const data = err as {
          detail?: string;
          mandates?: Record<string, FieldErrors>;
          scope?: Record<string, FieldErrors>;
        };
        setMandateErrors(data.mandates ?? {});
        setScopeErrors(data.scope ?? {});
        setFormError(data.detail ?? t("auth.genericError"));
      },
    });
  };

  // The pre-approved enjeux this step offers, named in a notice so the expert
  // knows no mandate is asked for them (the rest is still reviewed).
  const preapprovedNames = catalogue
    .flatMap((thematic) => thematic.issues)
    .filter((issue) => issue.has_goals)
    .filter((issue) => preapproved.has(issue.id) || issue.preapproved)
    .map((issue) => issue.name);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {t("expert.register.expertiseHelp")}
      </p>

      {preapprovedNames.length > 0 && (
        <div
          role="note"
          className="rounded-lg border border-info/40 bg-info/10 p-3 text-sm"
          data-testid="preapproved-notice"
        >
          <p className="flex items-center gap-1.5 font-semibold">
            <Info className="size-4" aria-hidden />
            {t("expert.register.preapprovedNoticeTitle")}
          </p>
          <p className="mt-1">
            {t("expert.register.preapprovedNotice", {
              names: preapprovedNames.join(", "),
            })}
          </p>
        </div>
      )}

      {catalogue.map((thematic) => {
        const theme = thematicClasses(thematic.css_class);
        const count = thematic.issues.filter((i) => selected[i.id]).length;
        return (
          <section key={thematic.id} className="space-y-2">
            <h3
              className={cn(
                "inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold",
                theme.softBg,
                theme.ink,
              )}
            >
              {thematic.name}
              {count > 0 && (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                  {count}
                </span>
              )}
            </h3>
            <div className="space-y-2">
              {thematic.issues.map((issue) => (
                <EnjeuRow
                  key={issue.id}
                  issue={issue}
                  state={selected[issue.id]}
                  preapproved={preapproved.has(issue.id) || issue.preapproved}
                  poolSectors={poolSectors}
                  poolOrgTypes={poolOrgTypes}
                  mandateErrors={mandateErrors[issue.id] ?? {}}
                  onMandateErrors={(errors) =>
                    setMandateErrors((prev) => {
                      const next = { ...prev };
                      if (Object.keys(errors).length > 0) next[issue.id] = errors;
                      else delete next[issue.id];
                      return next;
                    })
                  }
                  scopeErrors={scopeErrors[issue.id] ?? {}}
                  onToggle={(on) => toggleIssue(issue.id, on)}
                  onPatch={(updater) => patch(issue.id, updater)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {formError && (
        <p className="text-sm font-medium text-destructive">{formError}</p>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          {t("expert.register.back")}
        </Button>
        <Button onClick={handleContinue} disabled={save.isPending}>
          {submitMode
            ? t("expert.register.submitRequest")
            : t("expert.register.saveChanges")}
        </Button>
      </div>

      {declarationOpen && (
        <DeclarationModal
          profile={profile}
          // A draft or a rejected profile is (re)submitting for review, so the
          // modal PATCHes the declaration then POSTs the submit (not an edit).
          isEdit={false}
          onSubmitted={onSubmitted}
          onClose={() => setDeclarationOpen(false)}
          onOrganizationIncomplete={(message) => {
            setDeclarationOpen(false);
            onOrganizationIncomplete?.(message);
          }}
          onServiceRangeIncomplete={(message) => {
            setDeclarationOpen(false);
            onServiceRangeIncomplete?.(message);
          }}
        />
      )}
    </div>
  );
}

function EnjeuRow({
  issue,
  state,
  preapproved,
  poolSectors,
  poolOrgTypes,
  mandateErrors,
  onMandateErrors,
  scopeErrors,
  onToggle,
  onPatch,
}: {
  issue: CatalogueIssue;
  state: EnjeuState | undefined;
  preapproved: boolean;
  poolSectors: Ref[];
  poolOrgTypes: OrgType[];
  mandateErrors: FieldErrors;
  onMandateErrors: (errors: FieldErrors) => void;
  scopeErrors: FieldErrors;
  onToggle: (on: boolean) => void;
  onPatch: (updater: (e: EnjeuState) => EnjeuState) => void;
}) {
  const { t } = useTranslation();
  const [mandateOpen, setMandateOpen] = useState(false);
  // Per-enjeu scope inherits the registration scope and is shown open, so the
  // expert confirms it; it can be collapsed, but an error keeps it open.
  const [scopeOpen, setScopeOpen] = useState(true);
  const on = state !== undefined;
  const scopeShown = scopeOpen || Object.keys(scopeErrors).length > 0;
  const setScope =
    (key: "sectors" | "organization_types") =>
    (next: string[]) =>
      onPatch((e) => ({ ...e, [key]: next }));

  const mandate = state?.mandate;
  const hasMandate = Boolean(
    mandate &&
      (mandate.name.trim() ||
        mandate.year != null ||
        mandate.description.trim() ||
        mandate.clients.length),
  );
  const mandateIncomplete = Object.keys(mandateErrors).length > 0;

  return (
    <div
      className={cn(
        "rounded-lg border",
        on ? "border-primary/40 bg-card" : "border-border",
      )}
    >
      {/* Header: a light switch row — the heavy fields live in the modal below. */}
      <div className="flex items-center justify-between gap-3 p-3">
        <span className="font-medium">
          <label htmlFor={`issue-switch-${issue.id}`} className="cursor-pointer">
            {issue.name}
          </label>
          {preapproved && (
            <Badge tone="success" className="ml-2">
              {t("expert.register.preapproved")}
            </Badge>
          )}
        </span>
        <Switch
          id={`issue-switch-${issue.id}`}
          checked={on}
          onCheckedChange={onToggle}
          label={issue.name}
        />
      </div>

      {on && state && (
        <div className="space-y-4 border-t p-3">
          {preapproved && (
            <p className="text-sm text-muted-foreground">
              {t("expert.register.preapprovedNoMandate")}
            </p>
          )}
          {/* Reference mandate — a compact summary that opens the modal. */}
          {!preapproved && (
            <div>
              {hasMandate ? (
                <div className="flex items-start justify-between gap-3 rounded-md bg-secondary/40 p-2.5">
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {mandate?.name || t("expert.register.mandateUnnamed")}
                    </p>
                    {mandate?.description && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {mandate.description}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => setMandateOpen(true)}
                  >
                    <Pencil className="mr-1 size-4" aria-hidden />
                    {t("expert.register.editMandate")}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMandateOpen(true)}
                >
                  <Pencil className="mr-1 size-4" aria-hidden />
                  {t("expert.register.describeMandate")}
                </Button>
              )}
              {mandateIncomplete && (
                <ul className="mt-1.5 space-y-0.5">
                  {errorMessages(mandateErrors).map((msg, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-1 text-xs text-destructive"
                    >
                      <AlertCircle
                        className="mt-0.5 size-3.5 flex-shrink-0"
                        aria-hidden
                      />
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Scope defaults to the full registration pool, shown open so the
              expert confirms it for this enjeu (Django's « collapse show »). */}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0"
            aria-expanded={scopeShown}
            onClick={() => setScopeOpen(!scopeShown)}
          >
            <SlidersHorizontal className="size-4" aria-hidden />
            {t("expert.register.validateScope")}
          </Button>
          {scopeShown && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t("expert.register.scopeForIssueHelp")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <ScopeBox
                  label={t("expert.register.fields.issue_sectors")}
                  options={poolSectors.map((s) => ({ value: s.id, label: s.name }))}
                  selected={state.sectors}
                  onChange={setScope("sectors")}
                  error={scopeErrors.sectors}
                />
                <ScopeBox
                  label={t("expert.register.fields.issue_org_types")}
                  options={poolOrgTypes}
                  selected={state.organization_types}
                  onChange={setScope("organization_types")}
                  error={scopeErrors.org_types}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {mandateOpen && state && (
        <Modal
          title={`${t("expert.register.mandateModalTitle")} — ${issue.name}`}
          onClose={() => setMandateOpen(false)}
        >
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("expert.register.mandateHelp")}
            </p>
            <NumberRow
              label={t("expert.register.fields.mandate_count")}
              value={state.mandate_count}
              onChange={(v) => onPatch((e) => ({ ...e, mandate_count: v }))}
              // The column's ceiling, as Django's input caps it.
              max={2147483647}
              className="max-w-xs"
            />
            <p className="text-sm text-muted-foreground">
              {t("expert.register.mandateDescriptionHelp")}
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <TextRow
                  label={t("expert.register.fields.mandate_name")}
                  value={state.mandate.name}
                  onChange={(v) =>
                    onPatch((e) => ({ ...e, mandate: { ...e.mandate, name: v } }))
                  }
                  required
                  maxLength={255}
                  error={mandateErrors.name}
                />
              </div>
              {/* min/max hint at the range the server enforces; it is the rule. */}
              <NumberRow
                label={t("expert.register.fields.mandate_year")}
                value={state.mandate.year}
                onChange={(v) =>
                  onPatch((e) => ({ ...e, mandate: { ...e.mandate, year: v } }))
                }
                min={MANDATE_YEAR_MIN}
                max={mandateYearMax()}
                required
                error={mandateErrors.year}
              />
            </div>
            <div>
              <TextareaRow
                label={t("expert.register.fields.mandate_description")}
                // Required server-side, and marked as such on the Django step.
                required
                value={state.mandate.description}
                onChange={(v) =>
                  onPatch((e) => ({
                    ...e,
                    mandate: { ...e.mandate, description: v },
                  }))
                }
                wordLimit={LONG_TEXT_LIMIT}
                error={mandateErrors.description}
              />
            </div>
            <div>
              <CheckRow
                required
                label={t("expert.register.fields.mandate_accompaniment")}
                checked={state.mandate.is_accompaniment}
                onChange={(v) =>
                  onPatch((e) => ({
                    ...e,
                    mandate: { ...e.mandate, is_accompaniment: v },
                  }))
                }
              />
              {/* only accompaniment mandates are admissible. */}
              {mandateErrors.is_accompaniment && (
                <p className="mt-1 text-sm text-destructive">
                  {mandateErrors.is_accompaniment}
                </p>
              )}
            </div>
            <ClientList
              clients={state.mandate.clients}
              onChange={(clients) =>
                onPatch((e) => ({ ...e, mandate: { ...e.mandate, clients } }))
              }
            />
            <div className="flex justify-end border-t pt-4">
              <Button
                type="button"
                onClick={() => {
                  // Close only on a complete mandate; otherwise its errors
                  // show in place, where the expert can fix them.
                  const errors = validateMandate(state, !preapproved, t);
                  onMandateErrors(errors);
                  if (Object.keys(errors).length === 0) setMandateOpen(false);
                }}
              >
                {t("expert.register.done")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ClientList({
  clients,
  onChange,
}: {
  clients: MandateClient[];
  onChange: (clients: MandateClient[]) => void;
}) {
  const { t } = useTranslation();
  // Each label names its input, as the Django rows' `for`/`id` pairs do.
  const base = useId();
  const add = () =>
    onChange([...clients, { org_name: "", contact_name: "", phone: "", email: "" }]);
  const update = (index: number, patch: Partial<MandateClient>) =>
    onChange(clients.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const remove = (index: number) => onChange(clients.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <div>
        <Label className="block">{t("expert.register.fields.mandate_clients")}</Label>
        <p className="text-xs text-muted-foreground">
          {t("expert.register.clientsConfidential")}
        </p>
      </div>
      {clients.map((c, i) => (
        <div key={i} className="space-y-3 rounded-md border border-border p-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${base}-org-${i}`}>{t("expert.register.fields.client_org_name")}</Label>
            <Input
              id={`${base}-org-${i}`}
              maxLength={255}
              value={c.org_name}
              onChange={(e) => update(i, { org_name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${base}-contact-${i}`}>{t("expert.register.fields.client_contact_name")}</Label>
            <Input
              id={`${base}-contact-${i}`}
              maxLength={255}
              value={c.contact_name ?? ""}
              onChange={(e) => update(i, { contact_name: e.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`${base}-phone-${i}`}>{t("expert.register.fields.client_phone")}</Label>
              <Input
                id={`${base}-phone-${i}`}
                type="tel"
                inputMode="tel"
                value={c.phone}
                aria-invalid={
                  c.phone.trim() && !isRealisticPhone(c.phone.trim())
                    ? true
                    : undefined
                }
                onChange={(e) => update(i, { phone: e.target.value })}
              />
              {c.phone.trim() && !isRealisticPhone(c.phone.trim()) && (
                <p className="text-sm text-destructive">
                  {t("expert.register.validation.client_phone")}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${base}-email-${i}`}>{t("expert.register.fields.client_email")}</Label>
              <Input
                id={`${base}-email-${i}`}
                type="email"
                maxLength={254}
                value={c.email}
                aria-invalid={
                  c.email.trim() && !isValidEmail(c.email.trim())
                    ? true
                    : undefined
                }
                onChange={(e) => update(i, { email: e.target.value })}
              />
              {c.email.trim() && !isValidEmail(c.email.trim()) && (
                <p className="text-sm text-destructive">
                  {t("expert.register.validation.client_email")}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 size-4" aria-hidden />
              {t("expert.register.removeClient")}
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="mr-1 size-4" aria-hidden />
        {t("expert.register.addClient")}
      </Button>
    </div>
  );
}
